import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { EmployeeService } from "../../../../../../services/employee.service";
import { LogService } from "../../../../../../services/log.service";
import firebase from "firebase/compat/app";
import { CounsellorsMom, MomSessionItem } from "../../counsellors-mom.model";
import { CounsellorsMomService } from "../../counsellors-mom.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

/**
 * Component for adding/editing Counsellor MOM (Minutes of Meeting) sessions
 * Includes a table for multiple session items with topic, discussion summary, and person in charge
 */
@Component({
  selector: 'app-add-mom-counsellor',
  templateUrl: './add-mom-counsellor.component.html',
  styleUrls: ['./add-mom-counsellor.component.scss']
})
export class AddMomCounsellorComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();

  momForm!: FormGroup;
  allCounsellors: any[] = [];
  filteredCounsellors: any[] = [];
  participantSearch: string = '';
  currentEditId: any;
  isLoading: boolean = false;

  // Table columns for session items
  displayedColumns: string[] = ['topic', 'discussionSummary', 'personInCharge', 'actions'];

  // Data source for the table
  sessionDataSource: any[] = [];
  selectedParticipant: any;
  displayCentre = (c: any) => c;

  personInChargeSearch: string = '';
  filteredPersonInCharge: any[] = [];
  showAllPersonInCharge: { [key: number]: boolean } = {};
  userData = JSON.parse(localStorage.getItem('userData')!).userName

  constructor(
    private fb: FormBuilder,
    private counsellorsMomService: CounsellorsMomService,
    private employeeService: EmployeeService,
    private mLogService: LogService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AddMomCounsellorComponent>
  ) {}

  ngOnInit(): void {
    this.formInit();
    this.fetchAllCounsellors();

    // Get ID from dialog data for edit mode
    this.currentEditId = this.data?.id || null;

    if (this.data) {
      const record = this.data;

      // Patch basic fields
      this.momForm.patchValue({
        date: record.date?.toDate?.() || record.date,
        remarks: record.remarks,
        participants: record.participants || []
      });

      // Patch session items
      if (record.sessions && record.sessions.length > 0) {
        this.sessions.clear();
        record.sessions.forEach((session: MomSessionItem) => {
          this.sessions.push(this.createSessionItem(session));
        });
        this.updateDataSource();
      }
    }
  }

  /**
   * Initialize the form with validation
   */
  formInit() {
    this.momForm = this.fb.group({
      date: ['', Validators.required],
      participants: [[], Validators.required],
      remarks: [''],
      sessions: this.fb.array([])
    });
  }

  /**
   * Get sessions FormArray
   */
  get sessions(): FormArray {
    return this.momForm.get('sessions') as FormArray;
  }

  /**
   * Create a new session item form group
   */
  createSessionItem(session?: MomSessionItem): FormGroup {
    return this.fb.group({
      topic: [session?.topic || ''],
      discussionSummary: [session?.discussionSummary || '', Validators.required],
      personInCharge: [
        // Check if it's an array, if not convert to array, if empty string make empty array
        session?.personInCharge
          ? (Array.isArray(session.personInCharge) ? session.personInCharge : [session.personInCharge])
          : [],
        Validators.required
      ]
    });
  }

  /**
   * Add a new session row to the table
   */
  addSessionRow() {
    const newSession = this.createSessionItem();
    this.sessions.push(newSession);
    this.sessionDataSource = [...this.sessions.controls];
  }

  /**
   * Remove a session row from the table
   */
  removeSessionRow(index: number) {
    if (this.sessions.length > 0) {
      this.sessions.removeAt(index);
      this.sessionDataSource = [...this.sessions.controls];
      delete this.showAllPersonInCharge[index]; // ADD THIS LINE
    }
  }

  onPersonInChargeDropdownOpened(opened: boolean) {
    if (opened) {
      this.personInChargeSearch = '';
      this.filteredPersonInCharge = [...this.allCounsellors];
    }
  }

  togglePersonInChargeReadMore(event: MouseEvent, rowIndex: number): void {
    event.stopPropagation();
    this.showAllPersonInCharge[rowIndex] = !this.showAllPersonInCharge[rowIndex];
  }

  /**
   * Handle Person In-Charge search input change
   */
  onSearchPersonInChargeChange(event: Event, rowIndex: number) {
    const value = (event.target as HTMLInputElement).value.toLowerCase().trim();
    this.personInChargeSearch = value;

    if (!value) {
      this.filteredPersonInCharge = [...this.allCounsellors];
      return;
    }

    // Only show items that match the search
    this.filteredPersonInCharge = this.allCounsellors.filter(counsellor =>
      counsellor.toLowerCase().includes(value)
    );
  }


  getSelectedPersonInCharge(rowIndex: number): string[] {
    const sessionControl = this.sessions.at(rowIndex);
    return sessionControl?.get('personInCharge')?.value || [];
  }


  private updateDataSource() {
    this.sessionDataSource = [...this.sessions.controls];
  }


  submitForm() {
    if (this.momForm.invalid) {
      this.snackBar.open('Please fill all required fields', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.sessions.length === 0) {
      this.snackBar.open('Please add at least one session item', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isLoading = true;

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const username = `${userData.userName || ''}`.trim() || 'Unknown User';

    const payload: CounsellorsMom = {
      ...this.momForm.value,
      date: new Date(this.momForm.value.date)
    };

    if (this.currentEditId) {
      payload.updatedBy = username;
      payload.updatedAt = firebase.firestore.FieldValue.serverTimestamp();

      this.counsellorsMomService.update(this.currentEditId, payload)
        .then(() => {
          this.mLogService.addLog({
            date: Date.now(),
            section: 'Counsellor MOM Session',
            action: 'Updated',
            description: `${username} updated the Counsellor MOM Session`,
            currentIp: localStorage.getItem('currentip')!
          });

          this.afterSave('Updated successfully!');
        })
        .catch((error: any) => {
          this.isLoading = false;
          this.snackBar.open('Error updating session: ' + error.message, 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        });
    } else {
      payload.createdBy = username;
      payload.createdAt = firebase.firestore.FieldValue.serverTimestamp();
      payload.status = "Active";

      this.counsellorsMomService.create(payload)
        .then(() => {
          this.mLogService.addLog({
            date: Date.now(),
            section: 'Counsellor MOM Session',
            action: 'Created',
            description: `${username} created a new Counsellor MOM Session`,
            currentIp: localStorage.getItem('currentip')!
          });

          this.afterSave('Saved successfully!');
        })
        .catch((error: any) => {
          this.isLoading = false;
          this.snackBar.open('Error creating session: ' + error.message, 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        });
    }
  }


  private afterSave(message: string) {
    this.isLoading = false;
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['success-snackbar']
    });
    this.dialogRef.close(this.currentEditId ? 'updated' : 'saved');
    this.momForm.reset();
    this.currentEditId = null;
  }


  fetchAllCounsellors() {
    this.employeeService.getCounsellersAndKavita().pipe(takeUntil(this._destroyed$)).subscribe(counsellors => {
      this.allCounsellors = counsellors.map(counsellor => counsellor.userName);
      this.filteredCounsellors = [...this.allCounsellors];
      this.filteredPersonInCharge = [...this.allCounsellors]; // ADD THIS LINE
    });
  }


  onParticipantSearchChange(event: Event): void {
    this.participantSearch = (event.target as HTMLInputElement).value;
    this.filterParticipants();
  }

  //
  // filterParticipants(): void {
  //   const searchValue = this.participantSearch.toLowerCase().trim();
  //   if (!searchValue) {
  //     this.filteredCounsellors = [...this.allCounsellors];
  //   } else {
  //     this.filteredCounsellors = this.allCounsellors.filter(counsellor =>
  //       counsellor.toLowerCase().includes(searchValue)
  //     );
  //   }
  // }
  filterParticipants(): void {
    const searchValue = this.participantSearch.toLowerCase().trim();

    if (!searchValue) {
      this.filteredCounsellors = [...this.allCounsellors];
      return;
    }

    // Only show items that match the search
    this.filteredCounsellors = this.allCounsellors.filter(counsellor =>
      counsellor.toLowerCase().includes(searchValue)
    );
  }

  onParticipantDropdownOpened(opened: boolean) {
    if (opened) {
      this.participantSearch = '';
      this.filteredCounsellors = [...this.allCounsellors];
    }
  }


  cancel() {
    this.dialogRef.close();
  }


  onPersonInChargeSelectionChange(event: any, rowIndex: number): void {
    const selectedValues: string[] = event.value;
    const sessionControl = this.sessions.at(rowIndex);

    if (!sessionControl) return;

    if (selectedValues.includes('None')) {
      if (selectedValues.length > 1) {
        const lastSelected = selectedValues[selectedValues.length - 1];

        if (lastSelected === 'None') {
          sessionControl.get('personInCharge')?.setValue(['None']);
        } else {
          const filteredValues = selectedValues.filter(v => v !== 'None');
          sessionControl.get('personInCharge')?.setValue(filteredValues);
        }
      }
    }
  }


  isNoneDisabled(rowIndex: number): boolean {
    const sessionControl = this.sessions.at(rowIndex);
    if (!sessionControl) return false;

    const selectedValues: string[] = sessionControl.get('personInCharge')?.value || [];

    return selectedValues.length > 0 && !selectedValues.includes('None');
  }


  isPersonInChargeDisabled(rowIndex: number): boolean {
    const sessionControl = this.sessions.at(rowIndex);
    if (!sessionControl) return false;

    const selectedValues: string[] = sessionControl.get('personInCharge')?.value || [];

    return selectedValues.includes('None');
  }

  onParticipantsChange(selected: any[]) {
    this.momForm.get('participants')?.setValue(selected);
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

}



